import React, { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { DebitNote, Invoice } from '../../types';
import Button from '../Form/Button';
import Input from '../Form/Input';
import TextArea from '../Form/TextArea';
import { useClients } from '../../hooks/useClients';
import { useBranches } from '../../hooks/useBranches';
import { generateEnglishTemplate } from './DebitNoteLetterTemplate';
import { generateArabicTemplate } from './DebitNoteLetterTemplateAr';

interface DebitNoteLetterGeneratorProps {
  invoice: Invoice;
  initialLanguage?: 'en' | 'ar';
  onClose: () => void;
  onGenerate: (data: DebitNoteLetterData) => void;
}

interface DebitNoteLetterData {
  invoiceId: string;
  selectedDebitNotes: string[];
  language: 'en' | 'ar';
  customContent?: string;
  recipient?: string;
  notes?: string;
}

const DebitNoteLetterGenerator: React.FC<DebitNoteLetterGeneratorProps> = ({
  invoice,
  initialLanguage = 'en',
  onClose,
  onGenerate
}) => {
  const [selectedDebitNotes, setSelectedDebitNotes] = useState<string[]>([]);
  const [language, setLanguage] = useState<'en' | 'ar'>(initialLanguage);
  const [previewContent, setPreviewContent] = useState('');
  const { data: clientsData } = useClients();
  const { data: branchesData } = useBranches();

  const {
    register, handleSubmit,
    formState: { isSubmitting },
    watch, setValue
  } = useForm<DebitNoteLetterData>({
    defaultValues: {
      invoiceId: invoice.id, selectedDebitNotes: [],
      language: initialLanguage, customContent: '', recipient: '', notes: ''
    }
  });

  const client = useMemo(() => {
    if (!clientsData || clientsData.length === 0) return undefined;
    return clientsData.find((c: any) => (c.documentId || c.id) === invoice.clientId);
  }, [invoice.clientId, clientsData]);

  const vessel = useMemo(() => {
    if (!branchesData || branchesData.length === 0) return undefined;
    return branchesData.find((b: any) => (b.documentId || b.id) === invoice.vesselId);
  }, [invoice.vesselId, branchesData]);

  const debitNotes = useMemo(() => {
    if (!invoice.debitNotes || invoice.debitNotes.length === 0) return [];
    if (typeof invoice.debitNotes[0] === 'object') {
      return (invoice.debitNotes as any[]).map(note => {
        if (note.amount && typeof note.amount === 'object') return note as DebitNote;
        return {
          ...note,
          amount: { usd: note.amountUSD || note.amount?.usd || 0, egp: note.amountEGP || note.amount?.egp || 0 },
          deduction: { usd: note.deductionUSD || note.deduction?.usd || 0, egp: note.deductionEGP || note.deduction?.egp || 0 },
          paid: { usd: note.paidUSD || note.paid?.usd || 0, egp: note.paidEGP || note.paid?.egp || 0 },
          netRemaining: { usd: note.netRemainingUSD || note.netRemaining?.usd || 0, egp: note.netRemainingEGP || note.netRemaining?.egp || 0 }
        } as DebitNote;
      });
    }
    return [];
  }, [invoice.debitNotes]);

  const selectedNotes = useMemo(() => {
    return debitNotes.filter(note => selectedDebitNotes.includes(note.id));
  }, [debitNotes, selectedDebitNotes]);

  useEffect(() => {
    if (selectedNotes.length > 0) {
      const ctx = { clientName: client?.name, clientAddress: client?.address, invoiceId: invoice.id, vesselName: vessel?.name };
      const template = language === 'en' ? generateEnglishTemplate(selectedNotes, ctx) : generateArabicTemplate(selectedNotes, ctx);
      setPreviewContent(template);
      setValue('customContent', template);
    }
  }, [selectedNotes, language, setValue, client, vessel, invoice.id]);

  const handleDebitNoteToggle = (noteId: string) => {
    setSelectedDebitNotes(prev => prev.includes(noteId) ? prev.filter(id => id !== noteId) : [...prev, noteId]);
  };

  const handleSelectAll = () => {
    setSelectedDebitNotes(prev => prev.length === debitNotes.length ? [] : debitNotes.map(note => note.id));
  };

  const handleLanguageChange = (newLanguage: 'en' | 'ar') => {
    setLanguage(newLanguage);
    setValue('language', newLanguage);
  };

  const onSubmit = (data: DebitNoteLetterData) => {
    onGenerate({ ...data, selectedDebitNotes, language });
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const content = watch('customContent') || previewContent;
      printWindow.document.write(`<html><head><title>Debit Note Letter</title>
        <style>body { font-family: Arial, sans-serif; line-height: 1.6; margin: 40px; direction: ${language === 'ar' ? 'rtl' : 'ltr'}; }
        @media print { body { margin: 20px; } }</style></head>
        <body><pre style="white-space: pre-wrap;">${content}</pre></body></html>`);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Generate Debit Note Letter</h2>
              <p className="text-sm text-gray-600">Invoice #{invoice.id} - {client?.name} - {vessel?.name}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex flex-1 overflow-hidden">
            <div className="w-1/3 p-6 border-r border-gray-200 overflow-y-auto">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                  <div className="flex space-x-4">
                    {(['en', 'ar'] as const).map(lang => (
                      <button key={lang} type="button" onClick={() => handleLanguageChange(lang)}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${language === lang ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                        {lang === 'en' ? 'English' : 'العربية'}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">Select Debit Notes</label>
                    <button type="button" onClick={handleSelectAll} className="text-sm text-primary-600 hover:text-primary-700">
                      {selectedDebitNotes.length === debitNotes.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  {debitNotes.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No debit notes available for this invoice.</p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {debitNotes.map((note) => (
                        <label key={note.id} className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                          <input type="checkbox" checked={selectedDebitNotes.includes(note.id)} onChange={() => handleDebitNoteToggle(note.id)}
                            className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{note.description}</p>
                            <p className="text-xs text-gray-500">Date: {note.date} | ${note.netRemaining.usd.toFixed(2)} USD / {note.netRemaining.egp.toFixed(2)} EGP</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <Input label="Recipient (Optional)" {...register('recipient')} placeholder="Additional recipient information" />
                <TextArea label="Notes (Optional)" {...register('notes')} placeholder="Additional notes or instructions" rows={3} />
                <div className="flex space-x-3">
                  <Button type="submit" variant="primary" disabled={selectedDebitNotes.length === 0 || isSubmitting} className="flex-1">Generate Letter</Button>
                  <Button type="button" variant="secondary" onClick={handlePrint} disabled={selectedDebitNotes.length === 0} className="flex-1">Print</Button>
                </div>
              </form>
            </div>
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Letter Preview</h3>
                <p className="text-sm text-gray-600">{selectedDebitNotes.length} debit note(s) selected</p>
              </div>
              {selectedDebitNotes.length === 0 ? (
                <div className="text-center text-gray-500 mt-12">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>Select debit notes to preview the letter</p>
                </div>
              ) : (
                <div className="border rounded-lg p-6 bg-gray-50">
                  <TextArea {...register('customContent')} value={watch('customContent') || previewContent}
                    rows={25} className="font-mono text-sm" style={{ direction: language === 'ar' ? 'rtl' : 'ltr' }} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebitNoteLetterGenerator;
